import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getPublicCms } from '@/api/cms';
import {
  getPublicPointsOverview,
  type PublicPointsActivity,
  type PublicPointsLeaderboardItem,
  type PublicPointsRule,
  type PointsLevelInfo,
} from '@/api/points';

interface CommContent {
  tag: string;
  title: string;
  subtitle: string;
}

const EMPTY_COMM: CommContent = {
  tag: '',
  title: '',
  subtitle: '',
};

function readText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function normalizeCommContent(raw: unknown): CommContent {
  if (!raw || typeof raw !== 'object') return EMPTY_COMM;
  const parsed = raw as Record<string, unknown>;
  return {
    tag: readText(parsed.tag),
    title: readText(parsed.title),
    subtitle: readText(parsed.subtitle),
  };
}

function formatRelativeTime(rawTime: string | null): string {
  if (!rawTime) return '--';
  const date = new Date(rawTime);
  if (Number.isNaN(date.getTime())) return '--';
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return '刚刚';
  if (diff < 3600) return `${Math.floor(diff / 60)}分钟前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}小时前`;
  return `${Math.floor(diff / 86400)}天前`;
}

function resolveActivityVisual(actionKey: string, pointsDelta: number) {
  const key = String(actionKey || '').toLowerCase();
  if (key.includes('checkin')) {
    return { icon: 'ri-calendar-check-line', color: '#f97316' };
  }
  if (key.includes('share')) {
    return { icon: 'ri-share-line', color: '#0ea5e9' };
  }
  if (key.includes('like')) {
    return { icon: 'ri-thumb-up-line', color: '#ef4444' };
  }
  if (key.includes('invite')) {
    return { icon: 'ri-gift-2-line', color: '#10b981' };
  }
  if (key.includes('download') || key.includes('attachment')) {
    return { icon: 'ri-download-line', color: '#0ea5e9' };
  }
  if (key.includes('favorite') || key.includes('collect')) {
    return { icon: 'ri-heart-line', color: '#ef4444' };
  }
  return {
    icon: pointsDelta >= 0 ? 'ri-add-circle-line' : 'ri-subtract-line',
    color: pointsDelta >= 0 ? '#10b981' : '#ef4444',
  };
}

function formatSignedPoints(pointsDelta: number): string {
  const points = Number(pointsDelta);
  if (!Number.isFinite(points)) return '--';
  return points >= 0 ? `+${points}` : String(points);
}

export default function CommunitySection() {
  const { isLoggedIn } = useAuth();
  const [commContent, setCommContent] = useState<CommContent>(EMPTY_COMM);
  const [contentError, setContentError] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [levels, setLevels] = useState<PointsLevelInfo[]>([]);
  const [topList, setTopList] = useState<PublicPointsLeaderboardItem[]>([]);
  const [activities, setActivities] = useState<PublicPointsActivity[]>([]);
  const [pointsRules, setPointsRules] = useState<PublicPointsRule[]>([]);
  const [activeLevel, setActiveLevel] = useState<number | null>(null);
  const [feedVisible, setFeedVisible] = useState(false);

  const refreshContent = useCallback(async () => {
    try {
      const cms = await getPublicCms('home_page_content');
      setCommContent(normalizeCommContent(cms?.community));
      setContentError(null);
    } catch (err) {
      setCommContent(EMPTY_COMM);
      setContentError(err instanceof Error ? err.message : '首页社区区内容加载失败');
    }
  }, []);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const overview = await getPublicPointsOverview(10);
      setLevels(Array.isArray(overview.levels) ? overview.levels : []);
      setTopList(Array.isArray(overview.leaderboard) ? overview.leaderboard : []);
      setActivities(Array.isArray(overview.activities) ? overview.activities : []);
      setPointsRules(Array.isArray(overview.pointsRules) ? overview.pointsRules.filter((rule) => rule.enabled) : []);
      setFeedVisible(true);
    } catch (err) {
      console.error('Failed to load public points overview in community section:', err);
      setLevels([]);
      setTopList([]);
      setActivities([]);
      setPointsRules([]);
      setError(err instanceof Error ? err.message : '灵石数据加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshContent();
    void loadOverview();

    const handleRefresh = () => {
      void loadOverview();
    };
    const handleContentRefresh = () => void refreshContent();

    window.addEventListener('wanxiang_points_updated', handleRefresh);
    window.addEventListener('wanxiang_settings_updated', handleRefresh);
    window.addEventListener('wanxiang_feature_updated', handleRefresh);
    window.addEventListener('wanxiang_page_content_updated', handleContentRefresh);

    return () => {
      window.removeEventListener('wanxiang_points_updated', handleRefresh);
      window.removeEventListener('wanxiang_settings_updated', handleRefresh);
      window.removeEventListener('wanxiang_feature_updated', handleRefresh);
      window.removeEventListener('wanxiang_page_content_updated', handleContentRefresh);
    };
  }, [loadOverview, refreshContent]);

  const levelStats = useMemo(() => {
    return levels.map((level) => ({
      ...level,
      count: topList.filter((item) => item.level.level === level.level).length,
    }));
  }, [levels, topList]);

  const filteredTopList = useMemo(
    () => topList.filter((item) => (activeLevel ? item.level.level === activeLevel : true)),
    [activeLevel, topList],
  );

  return (
    <section className="py-24 bg-[#f8fafc]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#8b5cf6]/10 text-[#8b5cf6] text-sm font-medium mb-5">
            <i className="ri-rocket-2-line text-sm"></i>
            {commContent.tag || '无'}
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">
            {commContent.title.includes('修仙之路') ? (
              (() => {
                const parts = commContent.title.split('修仙之路');
                return (
                  <>
                    {parts[0]}
                    <span className="text-[#8b5cf6]">修仙之路</span>
                    {parts[1]}
                  </>
                );
              })()
            ) : commContent.title || '无'}
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">{commContent.subtitle || '无'}</p>
          {contentError && (
            <p className="text-xs text-red-500 mt-3">区块内容加载失败：{contentError}</p>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-12">
          {levelStats.length === 0 ? (
            <div className="col-span-6 bg-white border border-gray-100 rounded-2xl p-6 text-center text-sm text-gray-400">
              {loading ? '正在加载等级分布...' : error ? `等级数据加载失败：${error}` : '暂无等级数据'}
            </div>
          ) : levelStats.map((level) => (
            <button
              key={level.level}
              onClick={() => setActiveLevel(activeLevel === level.level ? null : level.level)}
              className={`relative p-4 rounded-2xl border-2 transition-all duration-300 cursor-pointer text-center group ${activeLevel === level.level
                  ? 'border-opacity-100 scale-105'
                  : 'border-gray-100 hover:border-opacity-50 hover:scale-102'
                }`}
              style={activeLevel === level.level ? { borderColor: level.color, backgroundColor: `${level.color}08` } : { backgroundColor: 'white' }}
            >
              <div
                className={`absolute inset-0 rounded-2xl transition-opacity duration-300 ${activeLevel === level.level ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}
                style={{ background: `radial-gradient(circle at 50% 0%, ${level.color}20, transparent 70%)` }}
              ></div>
              <div
                className="w-10 h-10 flex items-center justify-center rounded-xl mx-auto mb-3 relative z-10 transition-transform duration-300 group-hover:scale-110"
                style={{ backgroundColor: `${level.color}15` }}
              >
                <i className={`${level.icon} text-lg`} style={{ color: level.color }}></i>
              </div>
              <div className="text-sm font-bold relative z-10" style={{ color: level.color }}>{level.name}</div>
              <div className="text-xs text-gray-400 relative z-10 mt-0.5">{level.title}</div>
              <div
                className="mt-2 text-xs font-semibold px-2 py-0.5 rounded-full inline-block relative z-10"
                style={{ backgroundColor: `${level.color}15`, color: level.color }}
              >
                {level.minPoints === 0 ? '入门' : `${level.minPoints}+ 灵石`}
              </div>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
          <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-100 overflow-hidden flex h-full flex-col">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-xl">
                  <i className="ri-trophy-line text-amber-500"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">修仙排行榜</h3>
                  <p className="text-xs text-gray-400">真实灵石数据 · Top 10</p>
                </div>
              </div>
              {!isLoggedIn && (
                <Link
                  to="/login?tab=register"
                  className="text-xs text-[#8b5cf6] hover:text-[#7c3aed] cursor-pointer font-medium flex items-center gap-1 whitespace-nowrap"
                >
                  加入修炼<i className="ri-arrow-right-line text-xs"></i>
                </Link>
              )}
            </div>
            <div className="divide-y divide-gray-50 flex-1">
              {loading ? (
                <div className="py-12 text-center text-sm text-gray-400">正在加载排行榜...</div>
              ) : error ? (
                <div className="py-12 text-center text-sm text-red-500">排行榜加载失败：{error}</div>
              ) : topList.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-2xl mx-auto mb-3">
                    <i className="ri-seedling-line text-2xl text-gray-300"></i>
                  </div>
                  <p className="text-sm text-gray-400">还没有修炼者数据</p>
                </div>
              ) : filteredTopList.length === 0 ? (
                <div className="py-12 px-6 text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#8b5cf6]/8 rounded-2xl mx-auto mb-3">
                    <i className="ri-filter-off-line text-2xl text-[#8b5cf6]/45"></i>
                  </div>
                  <p className="text-sm font-medium text-gray-500">当前境界下还没有上榜成员</p>
                  <p className="text-xs text-gray-400 mt-1">可以切换其他境界，或等待新的灵石数据加入榜单。</p>
                </div>
              ) : filteredTopList
                .map((item, idx) => {
                  const rank = item.rank || idx + 1;
                  const nextLevel = levels.find((l) => l.level === item.level.level + 1);
                  const progressPct = nextLevel
                    ? Math.min(100, Math.round(((item.points - item.level.minPoints) / Math.max(1, nextLevel.minPoints - item.level.minPoints)) * 100))
                    : 100;
                  const rankIcon = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;
                  return (
                    <div key={`${item.rank}_${item.displayName}_${item.points}`} className="flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 transition-all">
                      <div className="w-7 text-center shrink-0">
                        {rankIcon ? <span className="text-lg">{rankIcon}</span> : <span className="text-sm font-bold text-gray-400">#{rank}</span>}
                      </div>
                      {item.avatarUrl ? (
                        <img src={item.avatarUrl} alt="avatar" className="w-9 h-9 rounded-xl object-cover shrink-0" />
                      ) : (
                        <div
                          className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-bold shrink-0"
                          style={{ backgroundColor: item.level.color }}
                        >
                          {item.displayName.charAt(0) || '道'}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900 truncate">{item.displayName}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${item.level.color}15`, color: item.level.color }}>
                            <i className={`${item.level.icon} text-xs mr-0.5`}></i>{item.level.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-[90px]">
                            <div
                              className="h-full rounded-full transition-all duration-700"
                              style={{ width: `${progressPct}%`, backgroundColor: item.level.color }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-400 truncate">{item.level.title}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-bold text-gray-900">{item.points.toLocaleString()}</div>
                        <div className="text-xs text-gray-400">灵石</div>
                      </div>
                    </div>
                  );
                })}
            </div>
            <div className="px-5 py-4 border-t border-gray-100 bg-gradient-to-r from-[#8b5cf6]/5 to-transparent shrink-0">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">榜单依据会员当前灵石数据实时更新</p>
                {!isLoggedIn ? (
                  <Link
                    to="/login?tab=register"
                    className="px-3 py-1.5 text-xs bg-[#8b5cf6] text-white rounded-lg hover:bg-[#7c3aed] transition-all cursor-pointer whitespace-nowrap font-medium"
                  >
                    立即加入
                  </Link>
                ) : (
                  <Link
                    to="/member?tab=points"
                    className="px-3 py-1.5 text-xs bg-[#8b5cf6]/10 text-[#8b5cf6] rounded-lg hover:bg-[#8b5cf6]/20 transition-all cursor-pointer whitespace-nowrap font-medium"
                  >
                    查看我的灵石
                  </Link>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4 h-full flex flex-col">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden flex-1 flex flex-col">
              <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#10b981] animate-pulse"></div>
                <h3 className="font-bold text-gray-900 text-sm">社区动态</h3>
              </div>
              <div className="divide-y divide-gray-50 flex-1">
                {loading ? (
                  <div className="px-4 py-8 text-center text-sm text-gray-400">正在加载动态...</div>
                ) : error ? (
                  <div className="px-4 py-8 text-center text-sm text-red-500">动态加载失败：{error}</div>
                ) : activities.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-gray-400">暂无动态记录</div>
                ) : activities.map((item, i) => {
                  const visual = resolveActivityVisual(item.actionKey, item.pointsDelta);
                  const pointsText = formatSignedPoints(item.pointsDelta);
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-all"
                      style={{
                        opacity: feedVisible ? 1 : 0,
                        transform: feedVisible ? 'translateX(0)' : 'translateX(20px)',
                        transitionDelay: `${i * 70}ms`,
                        transition: 'opacity 0.35s ease, transform 0.35s ease',
                      }}
                    >
                      <div
                        className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0"
                        style={{ backgroundColor: `${visual.color}15` }}
                      >
                        <i className={`${visual.icon} text-xs`} style={{ color: visual.color }}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-700 truncate">
                          <strong>{item.user}</strong>
                          {' '}获得{' '}
                          <span className={Number(item.pointsDelta) >= 0 ? 'text-[#10b981] font-semibold' : 'text-red-500 font-semibold'}>
                            {pointsText} 灵石
                          </span>
                          {' · '}
                          <span className="text-gray-600">{item.actionLabel || item.actionKey || '灵石变动'}</span>
                          {item.targetTitle ? <span className="text-gray-400">（{item.targetTitle}）</span> : null}
                        </p>
                      </div>
                      <span className="text-xs text-gray-400 shrink-0 whitespace-nowrap">{formatRelativeTime(item.createdAt)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-4 shrink-0">
              <h3 className="font-bold text-gray-900 text-sm mb-3 flex items-center gap-2">
                <i className="ri-copper-coin-line text-amber-500"></i>
                如何获取灵石
              </h3>
              <div className="space-y-2">
                {loading ? (
                  <div className="text-sm text-gray-400 py-4 text-center">正在加载规则...</div>
                ) : pointsRules.length === 0 ? (
                  <div className="text-sm text-gray-400 py-4 text-center">暂无规则配置</div>
                ) : pointsRules.map((rule) => (
                  <div key={rule.key} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl gap-3">
                    <div className="min-w-0">
                      <span className="text-xs font-medium text-gray-700 block truncate">{rule.label}</span>
                      <span className="text-[11px] text-gray-400 block truncate">{rule.dailyLimitText || '--'}</span>
                    </div>
                    <span className="text-xs font-bold text-[#10b981] shrink-0">{rule.rewardText || '--'}</span>
                  </div>
                ))}
              </div>
              {!isLoggedIn ? (
                <Link
                  to="/login?tab=register"
                  className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-[#8b5cf6] to-[#7c3aed] text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-rocket-2-line"></i>
                  开始修炼之旅
                </Link>
              ) : (
                <Link
                  to="/member?tab=points"
                  className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-[#8b5cf6] to-[#7c3aed] text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-sparkling-line"></i>
                  查看我的灵石
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
